import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, Shield, TrendingUp, Gift, Users, Mail } from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              AIでコンテンツ作成を自動化
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              ブログ記事、SNS投稿、メール文章を数秒で生成。もう文章作成に悩まない。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition"
              >
                <Sparkles className="mr-2" />
                無料で始める
              </Link>
              <a
                href="#beta-testers"
                className="inline-flex items-center bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-300 transition"
              >
                <Gift className="mr-2" />
                ベータテスター募集中
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">ContentCraft AIが選ばれる理由</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <Zap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">圧倒的な時短</h3>
              <p className="text-gray-600">
                数時間かかっていた文章作成がわずか数秒で完了
              </p>
            </div>
            <div className="text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">プロ品質の文章</h3>
              <p className="text-gray-600">
                読者を惹きつける高品質なコンテンツを生成
              </p>
            </div>
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">生産性向上</h3>
              <p className="text-gray-600">
                本業に集中できる時間を大幅に増やせます
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">シンプルな料金プラン</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4">無料プラン</h3>
              <p className="text-4xl font-bold mb-6">¥0<span className="text-lg">/月</span></p>
              <ul className="mb-8 space-y-2">
                <li>月間5回まで生成可能</li>
                <li>基本的なコンテンツタイプ</li>
                <li>メールサポート</li>
              </ul>
              <Link
                to="/register"
                className="block text-center bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300"
              >
                今すぐ始める
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg border-2 border-blue-600">
              <h3 className="text-2xl font-bold mb-4">ベーシック</h3>
              <p className="text-4xl font-bold mb-6">¥1,000<span className="text-lg">/月</span></p>
              <ul className="mb-8 space-y-2">
                <li>月間100回まで生成可能</li>
                <li>すべてのコンテンツタイプ</li>
                <li>優先サポート</li>
                <li>生成履歴の保存</li>
              </ul>
              <Link
                to="/register"
                className="block text-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
              >
                無料で試す
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4">プロ</h3>
              <p className="text-4xl font-bold mb-6">¥3,000<span className="text-lg">/月</span></p>
              <ul className="mb-8 space-y-2">
                <li>無制限に生成可能</li>
                <li>高度な機能</li>
                <li>24/7サポート</li>
                <li>APIアクセス</li>
              </ul>
              <Link
                to="/register"
                className="block text-center bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-900"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="beta-testers" className="py-16 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
            <Users className="h-16 w-16 text-orange-500 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              🎉 ベータテスター募集中！
            </h2>
            <p className="text-lg md:text-xl mb-8 text-gray-700">
              ContentCraft AIの開発に参加して、特別な特典を受け取りませんか？
            </p>
            
            <div className="bg-orange-50 rounded-lg p-6 mb-8">
              <h3 className="text-2xl font-bold mb-4">ベータテスター限定特典</h3>
              <ul className="text-left space-y-3 max-w-md mx-auto">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✅</span>
                  <span>Basic永年割引（月額1,000円→800円）</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✅</span>
                  <span>優先サポート対応</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✅</span>
                  <span>新機能の先行利用権</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✅</span>
                  <span>開発チームとの直接フィードバック</span>
                </li>
              </ul>
            </div>
            
            <p className="text-lg mb-6">
              <strong>先着100名様限定！</strong><br />
              現在の登録者数：<span className="text-2xl font-bold text-orange-600">12</span>/100名
            </p>
            
            <Link
              to="/register"
              className="inline-flex items-center bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-700 transition shadow-lg"
            >
              <Gift className="mr-2" />
              今すぐベータテスターに応募
            </Link>
            
            <p className="mt-6 text-sm text-gray-600">
              ※ ベータテスター期間中は無料でご利用いただけます
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">よくある質問</h2>
          
          <div className="space-y-6">
            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">無料プランでは何ができますか？</summary>
              <p className="mt-4 text-gray-600">
                無料プランでは月5回までコンテンツを生成できます。ブログ記事、SNS投稿、メール、商品説明のすべてのタイプが利用可能です。
                生成したコンテンツは商用利用も可能です。
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">生成されたコンテンツの著作権は？</summary>
              <p className="mt-4 text-gray-600">
                生成されたコンテンツの著作権はお客様に帰属します。商用利用、改変、再配布など、自由にご利用いただけます。
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">どのようなAIを使用していますか？</summary>
              <p className="mt-4 text-gray-600">
                最新のAI技術を活用し、日本語に最適化されたモデルを使用しています。
                自然で読みやすい日本語コンテンツを生成できるよう、継続的に改善を行っています。
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">生成に失敗した場合はどうなりますか？</summary>
              <p className="mt-4 text-gray-600">
                技術的な問題で生成に失敗した場合、その回数はカウントされません。
                エラーが続く場合は、サポートまでお問い合わせください。
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">プランの変更・解約はできますか？</summary>
              <p className="mt-4 text-gray-600">
                いつでもプランの変更・解約が可能です。日割り計算には対応していませんが、
                次回更新日まではご契約中のプランをご利用いただけます。
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">APIは提供されていますか？</summary>
              <p className="mt-4 text-gray-600">
                現在、プロプランのお客様向けにAPI提供を準備中です。
                ご興味のある方は、お問い合わせください。
              </p>
            </details>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">その他のご質問はこちらから</p>
            <a
              href="mailto:support@contentcraft.ai"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
            >
              <Mail className="mr-2 h-5 w-5" />
              support@contentcraft.ai
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;